import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Routes, Route, Link, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  User,
  Calendar,
  Clock,
  DollarSign,
  Star,
  Users,
  Settings,
  Award,
  MapPin,
  Phone,
  Mail,
  Edit,
  Plus,
  Trash2,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Loader2,
  Shield,
  ShieldCheck,
  FileCheck,
  Upload,
  Save,
  Activity,
  TrendingUp,
  Video,
  Image as ImageIcon,
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Sports list for selection
const SPORTS_LIST = [
  'Cricket', 'Football', 'Tennis', 'Badminton', 'Swimming',
  'Basketball', 'Volleyball', 'Athletics', 'Hockey', 'Rugby',
  'Golf', 'Squash', 'Table Tennis', 'Boxing', 'MMA',
  'Yoga', 'Pilates', 'Cycling', 'Running', 'Triathlon',
  'Weightlifting', 'CrossFit', 'Gymnastics', 'Martial Arts', 'Other'
];

// Subcategories
const SUBCATEGORIES = [
  { id: 'sports_equipment', name: 'Sports Equipment' },
  { id: 'fitness_gym', name: 'Fitness & Gym' },
  { id: 'outdoor_adventure', name: 'Outdoor & Adventure' },
  { id: 'cycling', name: 'Cycling' },
  { id: 'fishing_hunting', name: 'Fishing & Hunting' },
  { id: 'creative_hobbies', name: 'Creative Hobbies' },
  { id: 'gaming_esports', name: 'Gaming & E-Sports' },
  { id: 'water_sports', name: 'Water Sports' },
];

const CoachDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [coach, setCoach] = useState(null);
  const [coachId, setCoachId] = useState(localStorage.getItem('coach_id'));
  const [activeTab, setActiveTab] = useState('overview');

  // Check if coach is logged in
  useEffect(() => {
    const storedCoachId = localStorage.getItem('coach_id');
    if (!storedCoachId) {
      navigate('/coach/register');
      return;
    }
    setCoachId(storedCoachId);
    fetchCoachProfile(storedCoachId);
  }, [navigate]);

  const fetchCoachProfile = async (id) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/sports-services/coach/${id}`);
      setCoach(response.data);
    } catch (err) {
      console.error('Failed to fetch coach profile:', err);
      if (err.response?.status === 404) {
        localStorage.removeItem('coach_id');
        navigate('/coach/register');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('coach_id');
    navigate('/coach/register');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!coach) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="max-w-md">
          <CardContent className="p-6 text-center">
            <AlertTriangle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold">Coach Profile Not Found</h2>
            <p className="text-gray-500 mt-2">Please register as a coach first.</p>
            <Button onClick={() => navigate('/coach/register')} className="mt-4">
              Register as Coach
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="coach-dashboard">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center overflow-hidden">
              {coach.profile_image || coach.profile_photo ? (
                <img 
                  src={coach.profile_image || coach.profile_photo} 
                  alt={coach.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <User className="h-6 w-6 text-blue-600" />
              )}
            </div>
            <div>
              <h1 className="font-bold text-lg">{coach.name}</h1>
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Badge 
                  variant="outline" 
                  className={
                    coach.verification_status === 'verified' 
                      ? 'bg-green-50 text-green-700 border-green-200' 
                      : coach.verification_status === 'pending'
                      ? 'bg-yellow-50 text-yellow-700 border-yellow-200'
                      : 'bg-red-50 text-red-700 border-red-200'
                  }
                >
                  {coach.verification_status === 'verified' && <ShieldCheck className="h-3 w-3 mr-1" />}
                  {coach.verification_status}
                </Badge>
                <span className="text-gray-300">|</span>
                <span className="flex items-center gap-1">
                  <MapPin className="h-3 w-3" />
                  {coach.city}, {coach.country}
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Badge variant={coach.is_active ? 'default' : 'secondary'}>
              {coach.is_active ? 'Active' : 'Inactive'}
            </Badge>
            <Button variant="outline" size="sm" onClick={handleLogout}>
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Verification Banner */}
        {coach.verification_status === 'pending' && (
          <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium text-yellow-800">Verification Pending</p>
              <p className="text-sm text-yellow-700">
                Your profile is under review. Once verified, you will be visible to potential clients.
              </p>
            </div>
          </div>
        )}

        {/* Stats Overview */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Sessions</p>
                  <p className="text-2xl font-bold">{coach.total_sessions || 0}</p>
                </div>
                <Activity className="h-8 w-8 text-blue-200" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Rating</p>
                  <p className="text-2xl font-bold flex items-center">
                    {coach.rating?.toFixed(1) || '0.0'}
                    <Star className="h-5 w-5 text-yellow-400 ml-1" />
                  </p>
                </div>
                <span className="text-sm text-gray-400">({coach.review_count || 0} reviews)</span>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Services</p>
                  <p className="text-2xl font-bold">{coach.services?.length || 0}</p>
                </div>
                <FileCheck className="h-8 w-8 text-green-200" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Experience</p>
                  <p className="text-2xl font-bold">{coach.experience_years || 0} yrs</p>
                </div>
                <Award className="h-8 w-8 text-purple-200" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-4 mb-6">
            <TabsTrigger value="overview">
              <User className="h-4 w-4 mr-1" />
              Profile
            </TabsTrigger>
            <TabsTrigger value="services">
              <FileCheck className="h-4 w-4 mr-1" />
              Services ({coach.services?.length || 0})
            </TabsTrigger>
            <TabsTrigger value="availability">
              <Calendar className="h-4 w-4 mr-1" />
              Availability
            </TabsTrigger>
            <TabsTrigger value="verification">
              <Shield className="h-4 w-4 mr-1" />
              Verification
            </TabsTrigger>
          </TabsList>

          {/* Profile Tab */}
          <TabsContent value="overview">
            <CoachProfileEditor coach={coach} onUpdate={() => fetchCoachProfile(coachId)} />
          </TabsContent>

          {/* Services Tab */}
          <TabsContent value="services">
            <CoachServicesManager coachId={coachId} services={coach.services || []} onUpdate={() => fetchCoachProfile(coachId)} />
          </TabsContent>

          {/* Availability Tab */}
          <TabsContent value="availability">
            <CoachAvailabilityManager coachId={coachId} availability={coach.availability || {}} onUpdate={() => fetchCoachProfile(coachId)} />
          </TabsContent>

          {/* Verification Tab */}
          <TabsContent value="verification">
            <CoachVerificationStatus coach={coach} />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

// Profile Editor Component
const CoachProfileEditor = ({ coach, onUpdate }) => {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: coach.name || '',
    phone: coach.phone || '',
    bio: coach.bio || '',
    experience_years: coach.experience_years || 0,
    specializations: coach.specializations || [],
    certifications: coach.certifications || [],
    individual_rate: coach.pricing?.individual_rate || 0,
    group_rate: coach.pricing?.group_rate || 0,
    online_rate: coach.pricing?.online_rate || 0,
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/sports-services/coach/${coach.id}`, {
        name: form.name,
        phone: form.phone,
        bio: form.bio,
        experience_years: form.experience_years,
        specializations: form.specializations,
        certifications: form.certifications,
        pricing: {
          individual_rate: form.individual_rate,
          group_rate: form.group_rate,
          online_rate: form.online_rate,
        }
      });
      setEditing(false);
      onUpdate();
    } catch (err) {
      console.error('Failed to update profile:', err);
      alert('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Profile Information</CardTitle>
          <CardDescription>Manage your coach profile details</CardDescription>
        </div>
        <Button 
          variant={editing ? 'default' : 'outline'} 
          onClick={() => editing ? handleSave() : setEditing(true)}
          disabled={saving}
        >
          {saving ? (
            <Loader2 className="h-4 w-4 animate-spin mr-1" />
          ) : editing ? (
            <Save className="h-4 w-4 mr-1" />
          ) : (
            <Edit className="h-4 w-4 mr-1" />
          )}
          {editing ? 'Save Changes' : 'Edit Profile'}
        </Button>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid md:grid-cols-2 gap-6">
          {/* Basic Info */}
          <div className="space-y-4">
            <div>
              <Label>Full Name</Label>
              <Input 
                value={form.name}
                onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                disabled={!editing}
              />
            </div>
            <div>
              <Label>Phone</Label>
              <Input 
                value={form.phone}
                onChange={(e) => setForm(prev => ({ ...prev, phone: e.target.value }))}
                disabled={!editing}
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input value={coach.email} disabled />
              <p className="text-xs text-gray-400 mt-1">Email cannot be changed</p>
            </div>
            <div>
              <Label>Years of Experience</Label>
              <Input 
                type="number"
                value={form.experience_years}
                onChange={(e) => setForm(prev => ({ ...prev, experience_years: parseInt(e.target.value) || 0 }))}
                disabled={!editing}
              />
            </div>
          </div>

          {/* Pricing */}
          <div className="space-y-4">
            <h4 className="font-medium">Pricing (per session)</h4>
            <div>
              <Label>Individual Session Rate ({coach.country === 'UK' ? '£' : 'Rs'})</Label>
              <Input 
                type="number"
                value={form.individual_rate}
                onChange={(e) => setForm(prev => ({ ...prev, individual_rate: parseInt(e.target.value) || 0 }))}
                disabled={!editing}
              />
            </div>
            <div>
              <Label>Group Session Rate ({coach.country === 'UK' ? '£' : 'Rs'})</Label>
              <Input 
                type="number"
                value={form.group_rate}
                onChange={(e) => setForm(prev => ({ ...prev, group_rate: parseInt(e.target.value) || 0 }))}
                disabled={!editing}
              />
            </div>
            <div>
              <Label>Online Session Rate ({coach.country === 'UK' ? '£' : 'Rs'})</Label>
              <Input 
                type="number"
                value={form.online_rate}
                onChange={(e) => setForm(prev => ({ ...prev, online_rate: parseInt(e.target.value) || 0 }))}
                disabled={!editing}
              />
            </div>
          </div>
        </div>

        {/* Bio */}
        <div>
          <Label>Bio</Label>
          <Textarea 
            value={form.bio}
            onChange={(e) => setForm(prev => ({ ...prev, bio: e.target.value }))}
            disabled={!editing}
            rows={4}
            placeholder="Tell potential clients about yourself, your coaching philosophy, and experience..."
          />
        </div>

        {/* Specializations */}
        <div>
          <Label>Sports Specializations</Label>
          <div className="flex flex-wrap gap-2 mt-2">
            {form.specializations.map((spec, idx) => (
              <Badge key={idx} variant="secondary" className="flex items-center gap-1">
                {spec}
                {editing && (
                  <XCircle 
                    className="h-3 w-3 cursor-pointer hover:text-red-500"
                    onClick={() => setForm(prev => ({
                      ...prev,
                      specializations: prev.specializations.filter((_, i) => i !== idx)
                    }))}
                  />
                )}
              </Badge>
            ))}
            {editing && (
              <Select
                onValueChange={(v) => {
                  if (v && !form.specializations.includes(v)) {
                    setForm(prev => ({ ...prev, specializations: [...prev.specializations, v] }));
                  }
                }}
              >
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Add sport..." />
                </SelectTrigger>
                <SelectContent>
                  {SPORTS_LIST.filter(s => !form.specializations.includes(s)).map(sport => (
                    <SelectItem key={sport} value={sport}>{sport}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Services Manager Component
const CoachServicesManager = ({ coachId, services, onUpdate }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: '',
    description: '',
    service_type: 'coaching_individual',
    subcategory: 'fitness_gym',
    sports: [],
    duration: 60,
    price: 0,
    currency: 'Rs',
    max_participants: 1,
    is_active: true,
  });

  const resetForm = () => {
    setForm({
      name: '',
      description: '',
      service_type: 'coaching_individual',
      subcategory: 'fitness_gym',
      sports: [],
      duration: 60,
      price: 0,
      currency: 'Rs',
      max_participants: 1,
      is_active: true,
    });
    setEditingService(null);
  };

  const openEdit = (service) => {
    setEditingService(service);
    setForm({
      name: service.name,
      description: service.description || '',
      service_type: service.service_type,
      subcategory: service.subcategory,
      sports: service.sports || [],
      duration: service.duration,
      price: service.price,
      currency: service.currency || 'Rs',
      max_participants: service.max_participants || 1,
      is_active: service.is_active !== false,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editingService) {
        await axios.put(`${API_URL}/api/sports-services/coach/${coachId}/services/${editingService.id}`, form);
      } else {
        await axios.post(`${API_URL}/api/sports-services/coach/${coachId}/services`, form);
      }
      setDialogOpen(false);
      resetForm();
      onUpdate();
    } catch (err) {
      console.error('Failed to save service:', err);
      alert('Failed to save service');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (serviceId) => {
    if (!confirm('Are you sure you want to delete this service?')) return;
    try {
      await axios.delete(`${API_URL}/api/sports-services/coach/${coachId}/services/${serviceId}`);
      onUpdate();
    } catch (err) {
      console.error('Failed to delete service:', err);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold">My Services</h3>
          <p className="text-sm text-gray-500">Manage the services you offer to clients</p>
        </div>
        <Button onClick={() => { resetForm(); setDialogOpen(true); }}>
          <Plus className="h-4 w-4 mr-1" />
          Add Service
        </Button>
      </div>

      {services.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <FileCheck className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="font-medium text-gray-700">No Services Yet</h3>
            <p className="text-gray-500 mt-1">Add your first service to start accepting bookings</p>
            <Button className="mt-4" onClick={() => { resetForm(); setDialogOpen(true); }}>
              <Plus className="h-4 w-4 mr-1" />
              Add First Service
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {services.map((service) => (
            <Card key={service.id} className={!service.is_active ? 'opacity-60' : ''}>
              <CardContent className="p-4">
                <div className="flex justify-between items-start">
                  <div>
                    <h4 className="font-semibold">{service.name}</h4>
                    <p className="text-sm text-gray-500 mt-1">{service.description}</p>
                  </div>
                  <Badge variant={service.is_active ? 'default' : 'secondary'}>
                    {service.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
                <div className="mt-3 flex flex-wrap gap-2 text-sm text-gray-600">
                  <span className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {service.duration} mins
                  </span>
                  <span className="flex items-center gap-1">
                    <DollarSign className="h-3 w-3" />
                    {service.currency} {service.price}
                  </span>
                  <span className="flex items-center gap-1">
                    <Users className="h-3 w-3" />
                    Max {service.max_participants}
                  </span>
                </div>
                {service.sports?.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {service.sports.map((sport, idx) => (
                      <Badge key={idx} variant="outline" className="text-xs">{sport}</Badge>
                    ))}
                  </div>
                )}
                <div className="mt-4 flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => openEdit(service)}>
                    <Edit className="h-3 w-3 mr-1" />
                    Edit
                  </Button>
                  <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDelete(service.id)}>
                    <Trash2 className="h-3 w-3 mr-1" />
                    Delete
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Service Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingService ? 'Edit Service' : 'Add New Service'}</DialogTitle>
            <DialogDescription>
              {editingService ? 'Update your service details' : 'Create a new service offering for your clients'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Service Name *</Label>
              <Input 
                value={form.name}
                onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Personal Training Session"
              />
            </div>
            
            <div>
              <Label>Description</Label>
              <Textarea 
                value={form.description}
                onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Describe what clients can expect..."
                rows={3}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Service Type</Label>
                <Select 
                  value={form.service_type}
                  onValueChange={(v) => setForm(prev => ({ ...prev, service_type: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="coaching_individual">Individual Session</SelectItem>
                    <SelectItem value="coaching_group">Group Session</SelectItem>
                    <SelectItem value="coaching_online">Online Session</SelectItem>
                    <SelectItem value="coaching_camp">Camp / Clinic</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Category</Label>
                <Select 
                  value={form.subcategory}
                  onValueChange={(v) => setForm(prev => ({ ...prev, subcategory: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SUBCATEGORIES.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Duration (mins)</Label>
                <Input 
                  type="number"
                  value={form.duration}
                  onChange={(e) => setForm(prev => ({ ...prev, duration: parseInt(e.target.value) || 60 }))}
                />
              </div>
              <div>
                <Label>Price ({form.currency})</Label>
                <Input 
                  type="number"
                  value={form.price}
                  onChange={(e) => setForm(prev => ({ ...prev, price: parseInt(e.target.value) || 0 }))}
                />
              </div>
              <div>
                <Label>Max Participants</Label>
                <Input 
                  type="number"
                  value={form.max_participants}
                  onChange={(e) => setForm(prev => ({ ...prev, max_participants: parseInt(e.target.value) || 1 }))}
                />
              </div>
            </div>
            
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <p className="font-medium">Active</p>
                <p className="text-sm text-gray-500">Make this service visible to clients</p>
              </div>
              <Switch 
                checked={form.is_active}
                onCheckedChange={(v) => setForm(prev => ({ ...prev, is_active: v }))}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={!form.name || saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              {editingService ? 'Update Service' : 'Add Service'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Availability Manager Component
const CoachAvailabilityManager = ({ coachId, availability, onUpdate }) => {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [schedule, setSchedule] = useState(() => {
    const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    const defaultSchedule = {};
    days.forEach(day => {
      defaultSchedule[day] = {
        enabled: !!(availability[day] && availability[day].length > 0),
        slots: availability[day] || []
      };
    });
    return defaultSchedule;
  });

  const addSlot = (day) => {
    setSchedule(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        slots: [...prev[day].slots, '09:00-17:00']
      }
    }));
  };

  const updateSlot = (day, idx, value) => {
    setSchedule(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        slots: prev[day].slots.map((s, i) => i === idx ? value : s)
      }
    }));
  };

  const removeSlot = (day, idx) => {
    setSchedule(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        slots: prev[day].slots.filter((_, i) => i !== idx)
      }
    }));
  };

  const toggleDay = (day) => {
    setSchedule(prev => ({
      ...prev,
      [day]: {
        enabled: !prev[day].enabled,
        slots: prev[day].enabled ? [] : ['09:00-17:00']
      }
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const availabilityData = {};
      Object.entries(schedule).forEach(([day, data]) => {
        if (data.enabled && data.slots.length > 0) {
          availabilityData[day] = data.slots;
        }
      });
      
      await axios.put(`${API_URL}/api/sports-services/coach/${coachId}/availability`, availabilityData);
      setEditing(false);
      onUpdate();
    } catch (err) {
      console.error('Failed to update availability:', err);
      alert('Failed to save availability');
    } finally {
      setSaving(false);
    }
  };

  const dayLabels = {
    monday: 'Monday',
    tuesday: 'Tuesday',
    wednesday: 'Wednesday',
    thursday: 'Thursday',
    friday: 'Friday',
    saturday: 'Saturday',
    sunday: 'Sunday'
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Weekly Availability
          </CardTitle>
          <CardDescription>Set your available time slots for each day</CardDescription>
        </div>
        <Button 
          variant={editing ? 'default' : 'outline'} 
          onClick={() => editing ? handleSave() : setEditing(true)}
          disabled={saving}
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
          {editing ? 'Save Schedule' : 'Edit Schedule'}
        </Button>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {Object.entries(dayLabels).map(([day, label]) => (
            <div 
              key={day}
              className={`p-4 rounded-lg border ${schedule[day].enabled ? 'bg-white' : 'bg-gray-50'}`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  {editing && (
                    <Switch 
                      checked={schedule[day].enabled}
                      onCheckedChange={() => toggleDay(day)}
                    />
                  )}
                  <span className={`font-medium ${!schedule[day].enabled && 'text-gray-400'}`}>
                    {label}
                  </span>
                </div>
                {schedule[day].enabled && (
                  <Badge variant="outline" className="text-green-600">Available</Badge>
                )}
              </div>
              
              {schedule[day].enabled && (
                <div className="space-y-2 ml-10">
                  {schedule[day].slots.map((slot, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      {editing ? (
                        <>
                          <Input 
                            value={slot}
                            onChange={(e) => updateSlot(day, idx, e.target.value)}
                            placeholder="09:00-17:00"
                            className="w-40"
                          />
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => removeSlot(day, idx)}
                          >
                            <XCircle className="h-4 w-4 text-red-500" />
                          </Button>
                        </>
                      ) : (
                        <span className="text-sm text-gray-600 flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {slot}
                        </span>
                      )}
                    </div>
                  ))}
                  {editing && (
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => addSlot(day)}
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      Add Time Slot
                    </Button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

// Verification Status Component
const CoachVerificationStatus = ({ coach }) => {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Shield className="h-5 w-5" />
          Verification Status
        </CardTitle>
        <CardDescription>Your verification status and submitted documents</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Status Banner */}
        <div className={`p-4 rounded-lg ${
          coach.verification_status === 'verified' ? 'bg-green-50 border border-green-200' :
          coach.verification_status === 'pending' ? 'bg-yellow-50 border border-yellow-200' :
          'bg-red-50 border border-red-200'
        }`}>
          <div className="flex items-center gap-3">
            {coach.verification_status === 'verified' ? (
              <ShieldCheck className="h-8 w-8 text-green-600" />
            ) : coach.verification_status === 'pending' ? (
              <Shield className="h-8 w-8 text-yellow-600" />
            ) : (
              <XCircle className="h-8 w-8 text-red-600" />
            )}
            <div>
              <h3 className="font-semibold text-lg capitalize">{coach.verification_status}</h3>
              <p className="text-sm text-gray-600">
                {coach.verification_status === 'verified' 
                  ? 'Your profile is verified and visible to clients'
                  : coach.verification_status === 'pending'
                  ? 'Your verification is under review'
                  : 'Your verification was rejected'
                }
              </p>
            </div>
          </div>
        </div>

        {/* Certifications */}
        <div>
          <h4 className="font-medium mb-3">Certifications</h4>
          {coach.certifications?.length > 0 ? (
            <div className="space-y-2">
              {coach.certifications.map((cert, idx) => (
                <div key={idx} className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                  <Award className="h-4 w-4 text-blue-600" />
                  <span>{cert}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-sm">No certifications added yet</p>
          )}
        </div>

        {/* Trust Message */}
        <div className="p-4 bg-blue-50 rounded-lg">
          <h4 className="font-medium text-blue-900 mb-2">Building Trust</h4>
          <p className="text-sm text-blue-700">
            Complete your verification to build trust with potential clients. 
            Verified coaches receive more bookings and appear higher in search results.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default CoachDashboard;
